import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AppPreloader } from "../context/Preloader";
import { getMyProfile } from "../actions/user";
import { NoPermissionPage } from "../pages/auth";
import { hasPermission, isUserType } from "./authUtils";

interface ProtectedRouteProps {
  component: React.ComponentType<any>;
  permissions?: string | string[];
  logic?: "AND" | "OR";
  userType?: string | string[];
  [key: string]: unknown;
}

/**
 * @param {ReactNode} component - Component to render if authorized
 * @param {string|string[]} [permissions] - Required permission(s)
 * @param {'AND'|'OR'} [logic] - Logic to apply if multiple permissions are passed
 * @param {string|string[]} [userType] - Optional: required user type(s)
 * @returns {ReactNode}
 */
export const ProtectedRoute = ({
  component: Component,
  permissions: requiredPermissions = null,
  logic = "OR",
  userType = null,
  ...rest
}: ProtectedRouteProps) => {
  const dispatch = useDispatch();
  const requestedProfileRef = useRef(false);

  const { user, permissions: userPermissions } = useSelector((state) => state.user);

  useEffect(() => {
    if (user?.type) {
      requestedProfileRef.current = false;
      return;
    }

    if (!requestedProfileRef.current) {
      requestedProfileRef.current = true;
      dispatch(getMyProfile());
    }
  }, [user, dispatch]);

  if (!user?.type) {
    return <AppPreloader />;
  }

  if (!isUserType(userType, user.type)) {
    return <NoPermissionPage />;
  }

  if (requiredPermissions && !hasPermission(requiredPermissions, userPermissions, logic)) {
    return <NoPermissionPage />;
  }

  return <Component {...rest} />;
};
